import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError, to12h, dateStr } from '../lib/api';
import { startPayment } from '../lib/payments';
import { images } from '../data/mockData';
import {
  Waves, Activity, Heart, Droplets, Clock,
  Calendar as CalendarIcon, CheckCircle, ArrowRight, Loader2,
} from 'lucide-react';

const iconMap = {
  general: <Waves className="h-5 w-5" />,
  endurance: <Activity className="h-5 w-5" />,
  jacuzzi: <Heart className="h-5 w-5" />,
  therapy: <Droplets className="h-5 w-5" />,
};
const colorMap = {
  general: 'from-cyan-500 to-blue-500',
  endurance: 'from-teal-500 to-cyan-500',
  jacuzzi: 'from-pink-500 to-rose-500',
  therapy: 'from-purple-500 to-indigo-500',
};
const imageMap = {
  general: images.poolIndoor,
  endurance: images.poolTraining,
  jacuzzi: images.jacuzzi,
  therapy: images.poolUmbrella,
};
const featureMap = {
  general: ['Non-Chlorine Pool', 'Temperature Controlled', 'Lane Swimming', 'Open Swimming'],
  endurance: ['Counter-Current System', 'Resistance Training', 'Performance Monitoring', 'Equipment Included'],
  jacuzzi: ['Heated Jacuzzi', 'Hydrotherapy Jets', 'Relaxation Area', 'Towels Provided'],
  therapy: ['Certified Therapist', 'Personalized Program', 'Medical Equipment', 'Progress Tracking'],
};

const BookNowPage = ({ user, onOpenLogin }) => {
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedService, setSelectedService] = useState('general');
  const [slots, setSlots] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/bookings/services/list')
      .then((res) => {
        setServices(res.data);
        const param = searchParams.get('service');
        if (param && res.data.some((s) => s.id === param)) setSelectedService(param);
      })
      .catch((err) => toast({ title: 'Failed to load services', description: formatApiError(err), variant: 'destructive' }));
  }, [searchParams, toast]);

  // Load slot availability whenever service/date changes
  useEffect(() => {
    setSelectedTime('');
    api.get('/bookings/availability', { params: { date: dateStr(selectedDate), service_id: selectedService } })
      .then((res) => setSlots(res.data))
      .catch(() => setSlots([]));
  }, [selectedService, selectedDate]);

  useEffect(() => {
    if (user) { setName(user.name || ''); setPhone(user.phone || ''); }
  }, [user]);

  const activeService = services.find((s) => s.id === selectedService);

  const handleBooking = async () => {
    if (!user) {
      toast({ title: 'Please sign in to book', description: 'Login or create an account to confirm your booking.' });
      onOpenLogin?.();
      return;
    }
    if (!selectedTime) {
      toast({ title: 'Please select a time slot', description: 'Choose your preferred time to complete the booking.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post('/bookings', {
        booking_type: 'day_pass',
        service_id: selectedService,
        date: dateStr(selectedDate),
        start_time: selectedTime,
        name: name || user.name,
        phone: phone || user.phone,
        is_walkin: false,
      });

      // Proceed to payment
      try {
        const pay = await startPayment({
          purpose: 'booking',
          booking_id: booking.id,
          prefill: { name: name || user.name, email: user.email, contact: phone || user.phone || '' },
        });
        toast({
          title: `Payment Successful! 🎉${pay.mock ? ' (Test Mode)' : ''}`,
          description: `${activeService?.name} on ${selectedDate.toDateString()} at ${to12h(selectedTime)} · Invoice ${pay.invoice?.invoice_number}`,
        });
      } catch (payErr) {
        toast({
          title: 'Booking created — payment pending',
          description: `Your slot is reserved (${booking.booking_code}). ${formatApiError(payErr, 'Payment was not completed.')}`,
        });
      }

      // refresh availability
      const res = await api.get('/bookings/availability', { params: { date: dateStr(selectedDate), service_id: selectedService } });
      setSlots(res.data);
      setSelectedTime('');
    } catch (err) {
      toast({ title: 'Booking failed', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-56 overflow-hidden">
        <img src={images.poolIndoor} alt="Book Now" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-cyan-800/70" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="booknow-title">Book Your Session</h1>
            <p className="text-lg text-blue-100">Day pass access to our premium facilities</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-8 w-1.5 rounded-full bg-gradient-to-b from-cyan-500 to-blue-500" />
          <h2 className="text-2xl font-bold text-gray-900" data-testid="daypass-heading">Day Pass Services</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="service-selector">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setSelectedService(service.id)}
              className={`relative rounded-xl p-4 text-left border transition-all duration-200 ${
                selectedService === service.id ? 'border-cyan-400 bg-cyan-50 shadow-md' : 'border-gray-200 bg-white hover:border-cyan-200 hover:bg-gray-50'
              }`}
              data-testid={`service-btn-${service.id}`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorMap[service.id]} flex items-center justify-center text-white mb-3`}>
                {iconMap[service.id]}
              </div>
              <p className="font-semibold text-gray-900 text-sm">{service.name}</p>
              <p className="text-cyan-600 font-bold mt-1">{'\u20B9'}{service.price.toLocaleString('en-IN')}</p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-gray-200 overflow-hidden" data-testid="service-details-card">
              <div className="relative h-48">
                <img src={imageMap[selectedService]} alt={activeService?.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5 flex justify-between items-end">
                  <div>
                    <h3 className="text-xl font-bold text-white">{activeService?.name}</h3>
                    <p className="text-gray-200 text-sm">{activeService?.duration_min} min session</p>
                  </div>
                  <Badge className={`bg-gradient-to-r ${colorMap[selectedService]} text-white border-0 text-lg px-3`}>
                    {'\u20B9'}{activeService?.price.toLocaleString('en-IN')}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-5 space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">What's Included</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {(featureMap[selectedService] || []).map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  Duration: {activeService?.duration_min} minutes
                </div>

                <div>
                  <Label className="text-sm font-semibold mb-3 block">Select Time Slot</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="time-slots">
                    {slots.map((slot) => (
                      <Button
                        key={slot.time}
                        variant={selectedTime === slot.time ? 'default' : 'outline'}
                        size="sm"
                        disabled={!slot.available}
                        onClick={() => setSelectedTime(slot.time)}
                        className={`text-xs ${selectedTime === slot.time ? 'bg-cyan-500 hover:bg-cyan-600' : ''} ${!slot.available ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid={`time-${slot.time.replace(':', '-')}`}
                      >
                        {to12h(slot.time)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm">Full Name</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" data-testid="input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                    <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter phone number" data-testid="input-phone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-gray-200" data-testid="calendar-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CalendarIcon className="h-5 w-5 text-cyan-500" />
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  disabled={(date) => date < new Date(new Date().toDateString())}
                  className="rounded-md border-0"
                />
              </CardContent>
            </Card>

            <Card className="border border-cyan-200 bg-gradient-to-b from-cyan-50/50 to-white" data-testid="booking-summary">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Service</span><span className="font-medium">{activeService?.name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-medium">{selectedDate?.toDateString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Time</span><span className="font-medium">{selectedTime ? to12h(selectedTime) : 'Not selected'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Duration</span><span className="font-medium">{activeService?.duration_min} min</span></div>
                  <div className="border-t pt-3 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-cyan-600">{'\u20B9'}{activeService?.price.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <Button onClick={handleBooking} disabled={submitting || (user && !selectedTime)} className="w-full bg-cyan-500 hover:bg-cyan-600" size="lg" data-testid="confirm-booking-btn">
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {user ? `Pay \u20B9${(activeService?.price || 0).toLocaleString('en-IN')} & Book` : 'Sign in to Book'}
                  {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 gap-6">
          <Card className="bg-gradient-to-r from-cyan-600 to-teal-600 border-0 text-white">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Looking for Memberships?</h3>
                <p className="text-cyan-100 text-sm mt-1">Save more with monthly & yearly plans</p>
              </div>
              <Button asChild className="bg-white text-cyan-700 hover:bg-gray-50" size="sm">
                <a href="/membership">View Plans <ArrowRight className="ml-1 h-4 w-4" /></a>
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-teal-600 to-emerald-600 border-0 text-white">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Want Training?</h3>
                <p className="text-teal-100 text-sm mt-1">Professional coaching for all levels</p>
              </div>
              <Button asChild className="bg-white text-teal-700 hover:bg-gray-50" size="sm">
                <a href="/training">See Programs <ArrowRight className="ml-1 h-4 w-4" /></a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookNowPage;
